import { GanttUpdate, KanbanUpdate } from '@nexelem/graphne-api';
import { formatISO } from 'date-fns';

type GanttEventSourceConfig = {
  source: 'gantt';
  withDependenciesDetails: boolean;
  onUpdate: (data: GanttUpdate) => void;
};

type KanbanEventSourceConfig = {
  source: 'kanban';
  onUpdate: (data: KanbanUpdate) => void;
};

export type GraphneEventSourceConfig = (GanttEventSourceConfig | KanbanEventSourceConfig) & {
  from: Date;
  to: Date;
  queues?: string[];
  isBehindSchedule?: boolean;
  requiresAcceptance?: boolean;
  priority?: boolean;
  onlyQueuesWithTasks?: boolean;
};

type ListenerData = ((data: GanttUpdate) => void) | ((data: KanbanUpdate) => void);

export class GraphneEventSource {
  private eventSource: EventSource;

  constructor(private config: GraphneEventSourceConfig) {
    const url = this.getUrl();
    this.eventSource = new EventSource(url);

    if (config.onUpdate) {
      this.listen(config.onUpdate);
    }
  }

  private getUrl(): string {
    const url = new URL(`/graphne/${this.config.source}/updates`, window.location.origin);

    url.searchParams.append('from', formatISO(this.config.from));
    url.searchParams.append('to', formatISO(this.config.to));

    if (this.config.queues?.length) {
      this.config.queues.forEach((queueId) => {
        url.searchParams.append('queues', queueId);
      });
    }

    if ((this.config as GanttEventSourceConfig).withDependenciesDetails) {
      url.searchParams.append('withDependenciesDetails', 'true');
    }

    if (this.config.isBehindSchedule) {
      url.searchParams.append('isBehindSchedule', 'true');
    }
    if (this.config.requiresAcceptance) {
      url.searchParams.append('requiresAcceptance', 'true');
    }
    if (this.config.priority) {
      url.searchParams.append('priority', 'true');
    }
    if (this.config.onlyQueuesWithTasks) {
      url.searchParams.append('onlyQueuesWithTasks', 'true');
    }

    return url.toString();
  }

  listen(callback: ListenerData): void {
    this.eventSource.addEventListener('Full', (event) => {
      const data = JSON.parse(event.data);
      callback(data);
    });
  }

  close(): void {
    this.eventSource.close();
  }
}
